import { Check, Globe, Lock, Lock as LockIcon, Users as UsersIcon } from 'lucide-react';
import type { Application, Environment, HealthStatus, Network } from '../types';
import { cn, environmentLabel } from '../lib/utils';

/**
 * Pastille d'accès.
 *
 * Deux états seulement : j'y ai accès, ou je n'y ai pas accès. L'accès est
 * binaire (voir migration 022_droits_binaires.sql), il n'y a plus de demande
 * en cours à distinguer.
 */
export function AccessBadge({ application }: { application: Application }) {
    if (application.hasAccess) {
        return (
            <span className="badge badge-granted">
                <Check className="w-3 h-3" aria-hidden />
                Accès
            </span>
        );
    }

    return (
        <span className="badge badge-missing">
            <LockIcon className="w-3 h-3" aria-hidden />
            Sans accès
        </span>
    );
}

export function EnvironmentBadge({ environment, short = true }: { environment: Environment; short?: boolean }) {
    const isProd = environment === 'prod';
    return (
        <span className={cn('badge', isProd ? 'badge-prod' : 'badge-dev')}>
            {environmentLabel(environment, short)}
        </span>
    );
}

export function NetworkBadge({ network, requiresVpn = false }: { network: Network; requiresVpn?: boolean }) {
    if (network === 'private') {
        return (
            <span
                className="badge badge-private"
                title={
                    requiresVpn
                        ? 'Accessible depuis le réseau Clippit ou via le VPN'
                        : 'Accessible depuis le réseau interne uniquement'
                }
            >
                <Lock className="w-3 h-3" aria-hidden />
                Interne
            </span>
        );
    }

    return (
        <span className="badge badge-public" title="Accessible depuis Internet">
            <Globe className="w-3 h-3" aria-hidden />
            Public
        </span>
    );
}

const HEALTH_STYLES: Record<HealthStatus, { color: string; label: string }> = {
    up: { color: 'var(--color-success)', label: 'En ligne' },
    degraded: { color: 'var(--color-warning)', label: 'Réponse lente' },
    down: { color: 'var(--color-error)', label: 'Injoignable' },
    unknown: { color: 'var(--color-text-muted)', label: 'État inconnu' },
    unmonitored: { color: 'var(--color-text-muted)', label: 'Non supervisée' },
};

export function HealthDot({ status }: { status: HealthStatus }) {
    const style = HEALTH_STYLES[status];
    return (
        <span className="inline-flex items-center gap-1.5" title={style.label}>
            <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: style.color }}
                aria-hidden
            />
            <span className="text-xs text-[var(--color-text-secondary)]">{style.label}</span>
        </span>
    );
}

const STATUS_BADGE: Record<Application['status'], { className: string; label: string } | null> = {
    // « En service » est l'état normal : l'afficher partout serait du bruit.
    active: null,
    beta: { className: 'badge-dev', label: 'Bêta' },
    maintenance: { className: 'badge-pending', label: 'Maintenance' },
    deprecated: { className: 'badge-missing', label: 'Obsolète' },
};

export function StatusBadge({ status }: { status: Application['status'] }) {
    const config = STATUS_BADGE[status];
    if (!config) return null;
    return <span className={cn('badge', config.className)}>{config.label}</span>;
}

export function AccessModeBadge({ mode }: { mode: Application['accessMode'] }) {
    if (mode === 'everyone') {
        return <span className="badge badge-granted">Tout le monde</span>;
    }
    if (mode === 'entra_group') {
        return (
            <span className="badge badge-neutral" title="Accès synchronisé depuis un groupe Entra ID">
                <UsersIcon className="w-3 h-3" aria-hidden />
                Groupe Entra ID
            </span>
        );
    }
    return <span className="badge badge-neutral">Liste d’utilisateurs</span>;
}
